export type DurationPreference = "any" | "short" | "half" | "full";

/**
 * Weather-exposure preference. -2 = strongly prefer indoor / sheltered,
 * 0 = no preference (let weather + ranking decide), +2 = strongly prefer
 * outdoor / open-air. Stored as a number so we can scale ranking weights.
 */
export type WeatherExposure = -2 | -1 | 0 | 1 | 2;

export interface OriginAnswer {
  zip: string;
  lat: number;
  lng: number;
  placeName: string;
  /** Max one-way drive minutes the user is willing to travel. */
  maxDriveMinutes: number;
}

export interface WizardAnswers {
  timeframe: { start: string; end: string };
  duration: DurationPreference | null;
  budgetExclude: string[];
  weatherExposure: WeatherExposure;
  localPasses: string;
  /** Optional starting point for distance math. Null = anchor on Lakewood Ranch. */
  origin: OriginAnswer | null;
}

export const DEFAULT_WIZARD_ANSWERS: WizardAnswers = {
  timeframe: { start: "", end: "" },
  duration: null,
  budgetExclude: [],
  weatherExposure: 0,
  localPasses: "",
  origin: null,
};

/**
 * Parse a free-text estimated_duration string into approximate min/max hours.
 * Returns { min, max } in hours. Defaults to a wide range if unparseable.
 * Examples:
 *   "30 min" → { min: 0.5, max: 0.5 }
 *   "1–2 hrs" → { min: 1, max: 2 }
 *   "Half day" → { min: 3, max: 5 }
 *   "Multiple days" → { min: 8, max: 24 }
 */
export function parseDurationHours(text: string): { min: number; max: number } {
  if (!text) return { min: 1, max: 3 };
  const t = text.toLowerCase().trim();

  if (/multiple days|all day|whole day/.test(t)) return { min: 8, max: 24 };
  if (/half\s*day/.test(t)) return { min: 3, max: 5 };

  // Normalize separators
  const norm = t.replace(/[–—-]/g, "-");

  // Match ranges like "1-2 hrs", "30-60 min", "1.5-2.5 hrs", "30 min-1 hr"
  const rangeMatch = norm.match(/(\d+(?:\.\d+)?)\s*(min|hrs?|hours?)?\s*-\s*(\d+(?:\.\d+)?)\s*(min|hrs?|hours?)/);
  if (rangeMatch) {
    const lo = parseFloat(rangeMatch[1]);
    const hi = parseFloat(rangeMatch[3]);
    const loUnit = rangeMatch[2] || rangeMatch[4];
    const hiUnit = rangeMatch[4];
    const loHrs = /min/.test(loUnit) ? lo / 60 : lo;
    const hiHrs = /min/.test(hiUnit) ? hi / 60 : hi;
    return { min: loHrs, max: hiHrs };
  }

  // Match single value like "2 hrs", "30 min", "1.5 hrs"
  const singleMatch = norm.match(/(\d+(?:\.\d+)?)\s*(min|hrs?|hours?)/);
  if (singleMatch) {
    const v = parseFloat(singleMatch[1]);
    const unit = singleMatch[2];
    const hrs = /min/.test(unit) ? v / 60 : v;
    return { min: hrs, max: hrs };
  }

  return { min: 1, max: 3 };
}

/** Returns true if an event's duration matches the user's preference bucket. */
export function matchesDurationPreference(
  estimatedDuration: string,
  pref: DurationPreference,
): boolean {
  if (pref === "any") return true;
  const { min, max } = parseDurationHours(estimatedDuration);
  // Use the upper bound to gate "short" (so a "1-2 hr" event is short),
  // and lower bound for "full" (so "4-8 hr" qualifies as full).
  if (pref === "short") return max <= 2;
  if (pref === "half") return min >= 2 && max <= 5;
  if (pref === "full") return max >= 5 || /multiple days|all day|whole day/.test(estimatedDuration.toLowerCase());
  return true;
}

/**
 * Format an estimated_duration free-text string into a consistent "hrs"
 * representation. Always returns hours (never "Half day", "30 min", etc.).
 * Examples:
 *   "Half day" → "3–5 hrs"
 *   "30 min–1 hr" → "0.5–1 hrs"
 *   "2 hrs" → "2 hrs"
 *   "1–2 hrs" → "1–2 hrs"
 *   "Multiple days" → "8+ hrs"
 */
export function formatDurationHours(text: string): string {
  if (!text) return "1–3 hrs";
  const t = text.toLowerCase().trim();
  if (/multiple days|all day|whole day/.test(t)) return "8+ hrs";
  const { min, max } = parseDurationHours(text);
  const fmt = (n: number) => (Number.isInteger(n) ? `${n}` : `${n.toFixed(1).replace(/\.0$/, "")}`);
  if (min === max) return `${fmt(min)} hrs`;
  return `${fmt(min)}–${fmt(max)} hrs`;
}

export const AGE_RANGE_OPTIONS = [
  { label: "Toddler (0–2)", value: 0, emoji: "👶" },
  { label: "Preschool (3–5)", value: 3, emoji: "🧒" },
  { label: "Kids (6–12)", value: 6, emoji: "🎈" },
  { label: "Teens (13–17)", value: 13, emoji: "🎒" },
  { label: "Adults (18–64)", value: 18, emoji: "🧑" },
  { label: "Seniors (65+)", value: 65, emoji: "🧓" },
];

export const BUDGET_OPTIONS = [
  { label: "Free", value: "free" as const, emoji: "🆓" },
  { label: "Budget-Friendly", value: "low" as const, emoji: "💵" },
  { label: "Mid-Range", value: "medium" as const, emoji: "💰" },
  { label: "Splurge-Worthy", value: "high" as const, emoji: "💎" },
];

/** Known reciprocal pass relationships in the Sarasota/LWR area */
export const RECIPROCAL_PASSES: Record<string, {
  keywords: string[];
  reciprocalVenues: string[];
  benefit: string;
}> = {
  "selby gardens": {
    keywords: ["selby", "selby gardens", "marie selby"],
    reciprocalVenues: ["Historic Spanish Point", "Bok Tower Gardens", "Fairchild Tropical Botanic Garden", "Naples Botanical Garden"],
    benefit: "Free or discounted entry with Selby Gardens membership (AHS reciprocal)",
  },
  "mote marine": {
    keywords: ["mote", "mote marine", "mote aquarium"],
    reciprocalVenues: ["The Florida Aquarium", "Clearwater Marine Aquarium", "South Florida Museum", "Bishop Museum of Science and Nature"],
    benefit: "Free or discounted entry with Mote Marine membership (AZA reciprocal)",
  },
  "bishop museum": {
    keywords: ["bishop", "bishop museum", "south florida museum"],
    reciprocalVenues: ["Mote Marine Laboratory", "MOSI", "Orlando Science Center", "Ringling Museum"],
    benefit: "Free or discounted entry with Bishop Museum membership (ASTC reciprocal)",
  },
  "ringling museum": {
    keywords: ["ringling", "ringling museum", "ringling circus"],
    reciprocalVenues: ["Bishop Museum of Science and Nature", "The Dalí Museum", "Tampa Museum of Art"],
    benefit: "Free or discounted entry with Ringling membership (NARM reciprocal)",
  },
  "sarasota jungle gardens": {
    keywords: ["jungle gardens", "sarasota jungle"],
    reciprocalVenues: ["Flamingo Gardens", "Sunken Gardens", "Bok Tower Gardens"],
    benefit: "Reciprocal admission with AZA/AHS participating gardens",
  },
};
